import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, KeyRound, History, CreditCard } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PrivateBrowsingCard } from "@/components/analytics/PrivateBrowsingCard";
import { BlockedMembersCard } from "@/components/account/BlockedMembersCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/forms/Combobox";
import { PHONE_CODES } from "@/lib/phoneCodes";
import { Separator } from "@/components/ui/separator";
import { Loading } from "@/components/common/Loading";
import { AddressFields } from "@/components/forms/AddressFields";
import { DeleteAccountSection } from "@/components/account/DeleteAccountDialog";
import { EmailManagement } from "@/components/account/EmailManagement";
import { MfaSettings } from "@/components/account/MfaSettings";
import { OtterAssistPreferences } from "@/components/social/OtterAssistPreferences";
import { useReferences } from "@/hooks/useReferences";
import { toIana } from "@/lib/timezones";
import { useAuth } from "@/context/AuthContext";

const phoneCodeOptions = PHONE_CODES.map((c) => ({ value: c.code, label: c.code }));

export default function AccountSettings() {
  const { references } = useReferences();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const onboarding = params.get("onboarding") === "1";
  const [form, setForm] = useState(null);
  const [license, setLicense] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [activateKey, setActivateKey] = useState("");
  const [val, setVal] = useState(null);
  const [activating, setActivating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [mktBusy, setMktBusy] = useState(false);
  const [mktResub, setMktResub] = useState("");
  const valTimer = useRef();

  const loadLicense = async () => {
    try {
      const { data: lic } = await api.get("/license/me"); setLicense(lic);
      const { data: h } = await api.get("/license/history"); setHistory(h);
    } catch { /* no-op */ }
  };

  const reloadAccount = async () => {
    try { const { data } = await api.get("/account"); setForm(data); }
    catch { /* no-op */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/account");
        setForm(data);
        await loadLicense();
      } catch (e) {
        // A 401 is handled globally (auth cleared + redirect to login). Anything
        // else: stop the spinner so the page never hangs forever on "Loading".
        if (e?.response?.status !== 401) setLoadError(true);
      }
    })();
  }, []);

  const copyKey = () => {
    if (license?.formatted) {
      Promise.resolve(navigator.clipboard?.writeText(license.formatted))
        .then(() => toast.success("License key copied."))
        .catch(() => toast.error("Couldn't copy — copy it manually."));
    }
  };

  const onKeyChange = (v) => {
    setActivateKey(v);
    clearTimeout(valTimer.current);
    if (!v.trim()) { setVal(null); return; }
    valTimer.current = setTimeout(async () => {
      try { const { data } = await api.post("/license/validate", { key: v }); setVal(data); }
      catch { setVal({ valid: false, reason: "Could not validate that key." }); }
    }, 400);
  };

  const activate = async () => {
    setActivating(true);
    try {
      const { data } = await api.post("/license/redeem", { key: activateKey });
      toast.success(data.message || "Plan activated.");
      setActivateKey(""); setVal(null);
      await loadLicense();
      const { data: acc } = await api.get("/account"); setForm(acc);
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't activate that key.");
    } finally { setActivating(false); }
  };

  if (loadError) return (
    <div className="mx-auto max-w-3xl py-16 text-center text-sm text-muted-foreground" data-testid="account-load-error">
      Couldn’t load your account. Please refresh the page or sign in again.
    </div>
  );
  if (!form) return <Loading />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const timezones = references.timezones || [];
  // The account stores the real IANA zone (the reference label), never the slug.
  const tzOptions = timezones.map((t) => ({ value: t.label, label: t.label }));
  const countries = references.countries || [];

  const save = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!form.first_name?.trim() || !form.last_name?.trim()
        || !form.date_of_birth?.trim() || !form.country?.trim()) {
      toast.error("First name, last name, date of birth and country are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name, last_name: form.last_name,
        preferred_first_name: form.preferred_first_name, date_of_birth: form.date_of_birth,
        phone_country_code: form.phone_country_code, phone: form.phone,
        timezone: form.timezone, country: form.country, address: form.address,
        notification_prefs: form.notification_prefs, email_digest_prefs: form.email_digest_prefs,
      };
      const { data } = await api.put("/account", payload);
      setForm(data);
      await refreshUser();
      toast.success("Account settings saved.");
      if (onboarding) navigate("/dashboard?welcome=1");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  const np = form.notification_prefs || {};
  const NP_DEFAULTS = { messages: true, connections: true, questions: true, inquiries: true, events: false, product_updates: true, mentions: true, groups: true };
  const dp = form.email_digest_prefs || {};

  const toggleMarketing = async (v) => {
    setMktBusy(true);
    setMktResub("");
    try {
      const { data } = await api.post(v ? "/marketing/subscribe" : "/marketing/unsubscribe");
      set("marketing_opt_in", data.opt_in);
      if (data.resubscribe_required && data.resubscribe_url) {
        setMktResub(data.resubscribe_url);
        toast.info("You previously unsubscribed. Use the resubscribe link to opt back in.");
      } else {
        toast.success(v ? "You're subscribed to marketing emails." : "You've unsubscribed from marketing emails.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update your marketing preference.");
    } finally {
      setMktBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Account Settings</h1>
          <p className="mt-1 text-muted-foreground">Manage your personal account details and preferences.</p>
        </div>
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="account-save-button-top"
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <form onSubmit={save} className="mt-6 space-y-6">
        <Card className="p-6">
          <h2 className="text-lg font-[640] text-foreground">Personal details</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>First name *</Label><Input required maxLength={100} value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value.slice(0, 100))} data-testid="account-firstname" /></div>
            <div className="space-y-1.5"><Label>Last name *</Label><Input required maxLength={100} value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value.slice(0, 100))} data-testid="account-lastname" /></div>
            <div className="space-y-1.5"><Label>Preferred first name</Label><Input maxLength={100} value={form.preferred_first_name || ""} onChange={(e) => set("preferred_first_name", e.target.value.slice(0, 100))} data-testid="account-preferred" /></div>
            <div className="space-y-1.5"><Label>Date of birth *</Label><Input type="date" required value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="account-dob" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Email</Label><Input value={form.email || ""} disabled className="bg-secondary" /></div>
            <div className="space-y-1.5">
              <Label>Phone (optional)</Label>
              <div className="flex gap-2">
                <Combobox options={phoneCodeOptions} value={form.phone_country_code || "+1"} onChange={(v) => set("phone_country_code", v)} placeholder="Code" searchPlaceholder="Search dialing code…" emptyText="No dialing code found." testId="account-phone-code" className="w-28 shrink-0" />
                <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="Phone number" data-testid="account-phone" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Timezone</Label>
              <Combobox options={tzOptions} value={toIana(timezones, form.timezone)} onChange={(v) => set("timezone", v)} placeholder="Select timezone" searchPlaceholder="Search timezones…" emptyText="No timezone found." testId="account-timezone" />
              <p className="text-xs text-muted-foreground">Used across the platform — new events, Otter Assist and the times you see are all based on this.</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Country *</Label>
              <Combobox options={countries} value={form.country || ""} onChange={(v) => set("country", v)} placeholder="Select country" searchPlaceholder="Search countries…" emptyText="No country found." testId="account-country" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640] text-foreground">Address <span className="text-sm font-[400] text-muted-foreground">(optional)</span></h2>
          <div className="mt-4"><AddressFields value={form.address || {}} onChange={(a) => set("address", a)} testIdPrefix="account-address" /></div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640] text-foreground">Notification preferences</h2>
          <div className="mt-4 space-y-3">
            {[["messages", "New messages"], ["connections", "Connection requests"], ["mentions", "Mentions (when someone @tags you)"], ["groups", "Group activity (announcements in your groups)"], ["questions", "New questions"], ["inquiries", "Meeting & pitch inquiries"], ["events", "Event reminders"], ["product_updates", "Product updates"]].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between">
                <Label className="font-[440] text-foreground">{l}</Label>
                <Switch checked={k in np ? !!np[k] : NP_DEFAULTS[k]} onCheckedChange={(v) => set("notification_prefs", { ...np, [k]: v })} data-testid={`notif-${k}`} />
              </div>
            ))}
          </div>
          <Separator className="my-5" />
          <h2 className="text-lg font-[640] text-foreground">Email digest</h2>
          <div className="mt-4 flex items-center justify-between">
            <Label className="font-[440] text-foreground">Send me an email digest</Label>
            <Switch checked={!!dp.enabled} onCheckedChange={(v) => set("email_digest_prefs", { ...dp, enabled: v })} data-testid="digest-enabled" />
          </div>
          {dp.enabled && (
            <div className="mt-3 max-w-xs">
              <Label>Frequency</Label>
              <Select value={dp.frequency || "weekly"} onValueChange={(v) => set("email_digest_prefs", { ...dp, frequency: v })}>
                <SelectTrigger className="mt-1.5" data-testid="digest-frequency"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
              </Select>
            </div>
          )}
        </Card>

        <Card className="p-6" data-testid="account-marketing-card">
          <h2 className="text-lg font-[640] text-foreground">Marketing emails</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Product news, platform updates, community opportunities, and occasional promotions from Startup Otter.
          </p>
          <div className="mt-4 flex items-center justify-between">
            <Label className="font-[440] text-foreground">Send me marketing emails</Label>
            <Switch checked={!!form.marketing_opt_in} disabled={mktBusy} onCheckedChange={toggleMarketing} data-testid="marketing-opt-in-toggle" />
          </div>
          {mktResub && (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="marketing-resubscribe-note">
              You previously unsubscribed. <a href={mktResub} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="marketing-resubscribe-link">Resubscribe here</a>.
            </p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">You can change this anytime. See our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.</p>
        </Card>

        <Card className="p-6" data-testid="account-otter-assist-card">
          <h2 className="text-lg font-[640] text-foreground">Otter Assist preferences</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Control which Community Needs can reach you, how many a day, and when to pause. These are
            the same settings as Engagement → Otter Assist → Preferences.
          </p>
          <div className="mt-5">
            <OtterAssistPreferences />
          </div>
        </Card>

        <PrivateBrowsingCard />

        <BlockedMembersCard />

        <Card className="p-6" data-testid="account-license-card">
          <h2 className="text-lg font-[640] text-foreground">Membership & license</h2>
          <p className="mt-1 text-sm text-muted-foreground">Your current plan and personal license key.</p>
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Plan</span>
            <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50" data-testid="account-tier-badge">{license?.tier_name || "—"}</Badge>
            {license?.status === "disabled" && <Badge variant="secondary" className="text-amber-700">Disabled</Badge>}
          </div>
          {license?.expires_at && (
            <p className="mt-2 text-xs text-amber-600" data-testid="account-license-expiry">
              Access expires on {new Date(license.expires_at).toLocaleDateString()}.
            </p>
          )}
          <div className="mt-4">
            <Button type="button" variant="secondary" size="sm" onClick={() => navigate("/dashboard/billing")} data-testid="account-manage-billing">
              <CreditCard className="mr-1.5 h-3.5 w-3.5" /> Manage billing
            </Button>
          </div>
          <div className="mt-4">
            <Label>License key</Label>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <code className="rounded-md border border-border bg-secondary/60 px-3 py-2 font-mono text-sm tracking-[0.12em] text-foreground" data-testid="account-license-key">{license?.formatted || "—"}</code>
              <Button type="button" variant="secondary" size="sm" onClick={copyKey} disabled={!license?.formatted} data-testid="account-license-copy">
                <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Used to activate your plan and team seats. Keep it private — treat it like a password.</p>
          </div>

          <Separator className="my-5" />
          <div data-testid="account-activate-section">
            <Label className="flex items-center gap-1.5"><KeyRound className="h-4 w-4" /> Activate a license key</Label>
            <p className="mt-1 text-xs text-muted-foreground">Got a key? Enter it to switch your plan. It&apos;s checked before anything changes.</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input
                value={activateKey}
                onChange={(e) => onKeyChange(e.target.value)}
                placeholder="XXXXX-XXXXX-XXXXX"
                className="w-56 font-mono uppercase tracking-[0.12em]"
                data-testid="account-activate-input"
              />
              <Button type="button" onClick={activate} disabled={!val?.valid || activating} data-testid="account-activate-button">
                {activating ? "Activating…" : "Activate"}
              </Button>
            </div>
            {activateKey && val && (
              <p className={`mt-2 text-xs ${val.valid ? "text-teal-700" : "text-destructive"}`} data-testid="account-activate-feedback">
                {val.valid ? `Valid — activates the ${val.tier_name} plan.` : val.reason}
              </p>
            )}
          </div>

          {history.length > 1 && (
            <div className="mt-5">
              <button type="button" onClick={() => setShowHistory((s) => !s)} className="flex items-center gap-1.5 text-sm font-[560] text-foreground hover:text-primary" data-testid="account-history-toggle">
                <History className="h-4 w-4" /> Key history ({history.length})
              </button>
              {showHistory && (
                <ul className="mt-2 space-y-1.5" data-testid="account-history-list">
                  {history.map((h) => (
                    <li key={h.id} className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <code className="font-mono tracking-wider text-foreground/80">{h.formatted}</code>
                      <Badge variant="secondary" className="text-[10px]">{h.tier_name}</Badge>
                      <span className={h.status === "active" ? "text-teal-700" : ""}>{h.status}</span>
                      <span>· {String(h.issued_at).slice(0, 10)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-[640] text-foreground">Password</h2>
          <p className="mt-1 text-sm text-muted-foreground">Reset your password via a secure email link.</p>
          <Button type="button" variant="secondary" className="mt-3" onClick={async () => { await api.post("/auth/request-password-reset", { email: form.email }); toast.success("Password reset link sent to your email."); }} data-testid="account-reset-password">
            Send password reset link
          </Button>
        </Card>

        <EmailManagement
          email={form.email}
          recoveryEmail={form.recovery_email}
          recoveryVerified={form.recovery_email_verified}
          onChanged={reloadAccount}
        />

        <MfaSettings />

        <DeleteAccountSection />

        <div className="flex justify-end gap-2">
          <Button type="submit" size="lg" disabled={saving} data-testid="account-save-button">{saving ? "Saving…" : "Save changes"}</Button>
        </div>
      </form>
    </div>
  );
}
